import React, { FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
    Easing,
    FadeIn,
    FadeOut,
    SharedValue,
    useAnimatedStyle,
    useSharedValue,
    withSpring,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { NavigationRoute, ParamListBase, useNavigation } from '@react-navigation/native';

import { CustomFullWindowOverlay } from '@/components/CustomFullWindowOverlay';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { useSelectionCount } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { usePickbarAnimations } from '@/hooks/use-pickbar-animations';
import { isIos, isWeb } from '@/utils/constants-platform-specific';

import { PICK_SLIP_BAR_HEIGHT, PickSlipBar } from './PickSlipBar/PickSlipBar';

type SbkPickbarContextValue = {
    pickbarTransitionValue: SharedValue<number>;
};

// on iOS the event sheets are a native modal, the pickbar needs a full window overlay to show above them
// it stays mounted so the fade out still runs; on Android modals are normal views, the pickbar is already on top
const PickbarContainer = isIos ? CustomFullWindowOverlay : React.Fragment;

const SbkPickbarContext = React.createContext<SbkPickbarContextValue>({
    pickbarTransitionValue: { value: 0 } as SharedValue<number>,
});

export const SbkPickbarProvider: FC<PropsWithChildren> = ({ children }) => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const [routes, setRoutes] = useState<NavigationRoute<ParamListBase, string>[]>([]);
    const selectionCount = useSelectionCount();

    useEffect(() => {
        const unsubscribe = navigation.addListener('state', state => {
            setRoutes(state.data.state?.routes ?? []);
        });

        return unsubscribe;
    }, [navigation]);

    // determines if the user is on lobby and no other screen is on top of it
    const isOnLobby =
        routes.length === 1 &&
        (routes[0].name === 'SweepsHome' || routes[0].name === 'PredictionHome') &&
        routes[0].state?.index === 0;
    // only when the event sheets are the top screen, on iOS the pickbar is drawn over every screen (full window overlay)
    const isOnEventSheets = routes[routes.length - 1]?.name === 'SbkEventsSheets';
    const isPredictions = useIsPredictionsProduct();

    const shouldShowPickbar = (isOnLobby || isOnEventSheets) && !isPredictions;

    const pickbarTransitionValue = useSharedValue(-1);

    // Animation state for translateY spring
    const translateY = useSharedValue(selectionCount === 0 ? PICK_SLIP_BAR_HEIGHT * 2 + insets.bottom : 0);
    const prevSelectionCount = useSharedValue(selectionCount);
    const isDoingSpringAnimation = useSharedValue(false);

    const lobbyOffset = BOTTOM_TABS_HEIGHT + insets.bottom;
    const contentOffset = insets.bottom;

    const pickBarPaddingBottom = isOnLobby ? lobbyOffset : contentOffset;

    const { pickbarCustomLayout, animatedBottomStyle } = usePickbarAnimations(
        pickbarTransitionValue,
        contentOffset,
        lobbyOffset
    );

    // Animate translateY based on selection count changes
    useEffect(() => {
        const isZeroToOne = prevSelectionCount.value === 0 && selectionCount === 1;

        if (isZeroToOne) {
            // Spring animation when first selection is added
            isDoingSpringAnimation.value = true;
            translateY.value = withSpring(
                0,
                {
                    damping: 18,
                    stiffness: 400,
                    mass: 0.5,
                },
                finished => {
                    'worklet';
                    if (finished) {
                        isDoingSpringAnimation.value = false;
                    }
                }
            );
        } else {
            // Standard animation for all other transitions
            isDoingSpringAnimation.value = false;
            translateY.value = withTiming(selectionCount === 0 ? PICK_SLIP_BAR_HEIGHT * 2 + insets.bottom : 0, {
                duration: 100,
                easing: Easing.linear,
            });
        }

        prevSelectionCount.value = selectionCount;
    }, [selectionCount, translateY, prevSelectionCount, isDoingSpringAnimation, insets.bottom]);

    const animatedTranslateStyle = useAnimatedStyle(() => {
        return {
            transform: [{ translateY: translateY.value }],
        };
    });

    const contextValue = useMemo(
        () => ({
            pickbarTransitionValue,
        }),
        [pickbarTransitionValue]
    );

    return (
        <SbkPickbarContext value={contextValue}>
            {children}
            <PickbarContainer>
                {shouldShowPickbar ? (
                    <Animated.View
                        entering={isIos ? FadeIn : undefined}
                        exiting={isIos ? FadeOut : undefined}
                        pointerEvents={'box-none'}
                        layout={isWeb ? undefined : isDoingSpringAnimation.value ? undefined : pickbarCustomLayout}
                        style={[
                            styles.pickbarAnimatedContainer,
                            animatedBottomStyle,
                            animatedTranslateStyle,
                            {
                                paddingBottom: pickBarPaddingBottom,
                            },
                        ]}
                    >
                        <PickSlipBar />
                    </Animated.View>
                ) : null}
            </PickbarContainer>
        </SbkPickbarContext>
    );
};

export const useSbkPickbar = () => {
    const context = useContext(SbkPickbarContext);
    if (!context) {
        throw new Error('useSbkPickbar must be used within a SbkPickbarProvider');
    }
    return context;
};

const styles = StyleSheet.create({
    pickbarAnimatedContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
    },
});
